import * as THREE from 'three';
import { FACES } from '../Global/materials';

const TWOPI = 2 * Math.PI; // Full rotation in radians
const HALFPI = Math.PI / 2; // 90 degrees in radians

// Calculates the delta for Y rotation considering the current Z axis rotation
export function calculateYDelta(from, to, z) {
    if (to === Math.PI && from < 0) {
        to = - Math.PI; // Normalize wrapping across the -π/+π boundary
    }
    if (z !== 0) {
        // Adjust 'from' angle based on Z rotation
        if (from < 0) {
            from = - Math.PI - from;
        } else {
            from = Math.PI - from;
        }
    }
    const direct = to - from; // Direct angular difference
    return direct;
}

// Calculates delta for Z rotation, optionally compensating for side orientation
export function calculateZDelta(from, to, side) {
    let direct = to - from;
    if (side !== 0) direct -= Math.PI; // Adjust for side perspective if applicable
    return direct;
}

// General delta calculation between angles using shortest rotation path
export function calculateDelta(from, to) {
    const direct = to - from;
    const altA = direct - TWOPI; // One full rotation back
    const altB = direct + TWOPI; // One full rotation forward
    // Return the shortest rotation
    if (Math.abs(direct) > Math.abs(altA)) {
        return altA;
    }
    else if (Math.abs(direct) > Math.abs(altB)) {
        return altB;
    }
    return direct;
}

// Translates Y rotation to an equivalent Z rotation value, used for ring positioning
function translateYToZ(y, z) {
    if (z !== 0) {
        if (y === 0) {
            return Math.PI;
        } else if (y < 0) {
            return Math.abs(- Math.PI - y); // Adjust for negative rotation
        } else {
            return (Math.PI + y); // Adjust for positive rotation
        }
    } else {
        if (y === 0) {
            return 0;
        } else if (y < 0) {
            return Math.abs(y); // Mirror for negative rotation
        } else {
            return (TWOPI) - y; // Wrap around full circle
        }
    }
}

// Rotates the cube using a quaternion and updates visibility of 'top' and '3D' faces
export function moveCube(quat, cube) {
    const initialQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(HALFPI, 0, 0));
    // Apply inverse quaternion rotation and compensate with initial orientation
    cube.setQuaternion(quat.invert().multiply(initialQuat));

    const obj = cube.getObject(); // Get the cube object with faces
    // Determine which face should be visible based on X rotation
    if (Math.abs(obj.rotation.x).toFixed(3) === HALFPI.toFixed(3)) {
        obj.getObjectByName(FACES.top).visible = false;
        obj.getObjectByName(FACES.threed).visible = true;
    } else {
        obj.getObjectByName(FACES.threed).visible = false;
        obj.getObjectByName(FACES.top).visible = true;
    }
}

// Rotates the ring to follow the cube's rotation around the Z axis
export function moveRing(quat, cube, ring) {
    const cubeRotation = cube.getObject().rotation;
    const z = Number(new THREE.Euler().setFromQuaternion(quat).z.toFixed(0)); // Get Z rotation in degrees
    ring.getObject().rotation.z = -translateYToZ(cubeRotation.y, z); // Apply translated rotation
}
